/**
 * Phase 1 launch scope: Individual Learners only.
 *
 * All Educator, Institution, Admin, Partner API, Coach, and internal
 * surfaces are hidden from end users during Phase 1. This file is the
 * single source of truth for what a signed-out or signed-in learner is
 * allowed to reach.
 *
 * To lift the restriction later, flip PHASE_1_LEARNER_ONLY to false.
 */

export const PHASE_1_LEARNER_ONLY = true;

/** Exact paths that are always allowed (no prefix match). */
const EXACT_ALLOW = new Set<string>([
  "/",
  "/login",
  "/auth",
  "/auth/age",
  "/auth/callback",
  "/reset-password",
  "/forgot-password",
  "/pending-approval",
  "/learn",
  "/my-learning",
  "/plan",
  "/saved",
  "/rewards",
  "/pricing",
  "/faq",
  "/about",
  "/unsubscribe",
  "/privacy",
  "/terms",
  "/refunds",
  "/coppa",
  "/accessibility",
  "/trust",
  "/frameworks",
  "/bgmi",
  "/free-fire",
  "/for-organizations",
  "/for-institutions",
  "/for-educators",
  "/for-parents",
  "/connect/arena-id",
  // Authenticated learner surfaces inside AppLayout.
  "/app",
  "/app/home",
  "/app/settings",
  "/app/purchases",
  "/app/support",
  "/app/certificates",
  "/app/browse",
  "/app/community",
  "/app/dashboard",
  "/app/rewards",
  "/app/saved",
  "/app/plan",
  "/welcome",
]);

/** Prefixes that are allowed (path.startsWith(prefix)). */
const PREFIX_ALLOW: string[] = [
  "/learn/",              // pathway detail, module reader, capstone, placement, cert checkout
  "/app/pathways/",       // authenticated shell copies of pathway detail + module reader
  "/verify/",
  "/embed/",
  "/credentials/",
  "/certificate/",
  "/checkout/pathway/",   // W3.4 paid pathway checkout
];

export function isLearnerAllowedPath(pathname: string): boolean {
  if (!PHASE_1_LEARNER_ONLY) return true;
  if (EXACT_ALLOW.has(pathname)) return true;
  return PREFIX_ALLOW.some((p) => pathname.startsWith(p));
}

/**
 * Non-learner surfaces that the Phase 1 gate actively redirects.
 * Anything outside this list that also isn't allowed is simply an unknown
 * URL, so it must fall through to the 404 page instead of silently
 * bouncing the visitor to /learn.
 */
const GATED_PREFIXES: string[] = [
  "/app",
  "/admin",
  "/educator",
  "/institution",
  "/coach",
  "/partner",
  "/internal",
  "/for-",
  "/api",
  "/developers",
  "/org",
  "/team",
];

export function isPhase1GatedPath(pathname: string): boolean {
  if (!PHASE_1_LEARNER_ONLY) return false;
  if (isLearnerAllowedPath(pathname)) return false;
  return GATED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

